'use client';

import { useState, useEffect } from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { cn } from '@/app/utils/cn';

const MOBILE_BREAKPOINT = 768;

function CarouselWrapper({ children, totalItems, gapRem = 1.5 }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const visibleCards = isMobile ? 1 : 3;

    const maxIndex = Math.max(0, totalItems - visibleCards);
    const safeCurrentIndex = Math.min(currentIndex, maxIndex);
    const canGoBack = safeCurrentIndex > 0;
    const canGoForward = safeCurrentIndex < maxIndex;

    const handlePrev = () => {
        if (canGoBack) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    const handleNext = () => {
        if (canGoForward) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const cardWidthCalc = `(100% - ${(visibleCards - 1) * gapRem}rem) / ${visibleCards}`;
    const slideAmount = `calc((${cardWidthCalc}) + ${gapRem}rem)`;

    return (
        <div className={cn('w-full')}>
            <div className="mb-4 flex justify-end">
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handlePrev}
                        disabled={!canGoBack}
                        aria-label="Previous item"
                        className={cn(
                            'flex size-10 items-center justify-center rounded-full border border-gray-300 transition-colors',
                            canGoBack
                                ? 'bg-white text-gray-700 hover:bg-gray-100'
                                : 'cursor-not-allowed bg-gray-100 text-gray-300'
                        )}>
                        <IconChevronLeft className="size-5" />
                    </button>
                    <button
                        type="button"
                        onClick={handleNext}
                        disabled={!canGoForward}
                        aria-label="Next item"
                        className={cn(
                            'flex size-10 items-center justify-center rounded-full border border-gray-300 transition-colors',
                            canGoForward
                                ? 'bg-white text-gray-700 hover:bg-gray-100'
                                : 'cursor-not-allowed bg-gray-100 text-gray-300'
                        )}>
                        <IconChevronRight className="size-5" />
                    </button>
                </div>
            </div>
            <div className="overflow-x-hidden px-5 pb-7">
                <ul
                    className="flex gap-6 py-4 transition-transform duration-300 ease-in-out"
                    style={{
                        transform: `translateX(calc(-${safeCurrentIndex} * ${slideAmount}))`
                    }}>
                    {children(cardWidthCalc)}
                </ul>
            </div>
        </div>
    );
}

export default CarouselWrapper;
